import { MultiStepFormDialog } from '@components/MultiStepForms/MultiStepFormDialog';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { DialogProps } from '@mui/material';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { DomainError } from 'gql/graphql-fetcher';
import { StartWorkflowInstanceError, useStartWorkflowMutation, useWorkflowInstancesQuery, WorkflowExecutionFatalError } from 'gql/index';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ConfigureVariablesStep } from './components/ConfigureVariablesStep';
import { SelectWorkflowTemplateStep } from './components/SelectWorkflowTemplateStep';
import { StartWorkflowFormValues } from './types';

export const StartWorkflowDialog: React.FC<DialogProps> = (dialogProps) => {
  const { formatMessage } = useIntl();
  const invalidateQuery = useQueryInvalidator();
  const { projectId } = useCurrentProject();
  const navigate = useNavigate();
  const [isWorkflowMisconfigured, setIsWorkflowMisconfigured] = useState(false);
  
  const form = useForm<StartWorkflowFormValues>({});

  useEffect(() => {
    if (!dialogProps.open) {
      setIsWorkflowMisconfigured(false);
      form.reset();
    }
  }, [dialogProps.open, form]);

  const { mutate: startWorkflow, isPending: isStartingWorkflow } = useStartWorkflowMutation();

  const onStartWorkflow = (values: StartWorkflowFormValues) => {
    setIsWorkflowMisconfigured(false);
    startWorkflow({
      input: {
        projectId: projectId,
        workflowDefinitionId: values.workflowTemplateId,
        workflowVariables: values.variableInputs.filter(p => typeof p.id !== 'string').map(p => ({
          variableId: p.id as number,
          serializedValue: p.value == null ? null : JSON.stringify(p.value)
        }))
      }
    }, {
      onSuccess: e => {
        invalidateQuery(useWorkflowInstancesQuery);
        const id = e.startWorkflowInstance.workflowInstance?.id;
        navigate(`/${getTenantIdentifier()}/projects/${projectId}/workflows/instances/${id}`);
      },
      onError: (error) => {
        if (error instanceof DomainError) {
          const errors = error.errors
            .map(p => p as StartWorkflowInstanceError);

          if (errors.some(p => p.__typename == 'WorkflowConfigurationError')) {
            setIsWorkflowMisconfigured(true);
          } else {
            const fatalError = errors.find(p => p.__typename == 'WorkflowExecutionFatalError') as WorkflowExecutionFatalError | undefined;
            if (fatalError != null) {
              invalidateQuery(useWorkflowInstancesQuery);
              const id = fatalError.workflowInstanceId;
              navigate(`/${getTenantIdentifier()}/projects/${projectId}/workflows/instances/${id}`);
            }
          }
        }
      }
    });
  };

  const selectedTemplateId = form.watch('workflowTemplateId');

  return (
    <FormProvider {...form}>
      <MultiStepFormDialog
        dialogProps={dialogProps}
        disableSubmit={isWorkflowMisconfigured}
        onSubmit={form.handleSubmit(onStartWorkflow)}
        isSubmitting={isStartingWorkflow}
        steps={[
          {
            title: formatMessage({ id: 'Select a workflow' }),
            content: () => <SelectWorkflowTemplateStep />,
            onNext: (callback) => {
              setIsWorkflowMisconfigured(false);
              return form.handleSubmit(callback);
            },
            disableNext: selectedTemplateId == null
          },
          {
            title: formatMessage({ id: 'Configure workflow variables' }),
            content: () => <ConfigureVariablesStep isWorkflowMisconfigured={isWorkflowMisconfigured} />,
          }
        ]}
      />
    </FormProvider>
  );
};